/** Gemeinsame Typen und Helfer der Sportseiten. */
import type { AttendanceBand, RosterStatus, TeamDivision } from '@ukinfo/shared';

export interface Team {
  id: string;
  name: string;
  organisation_unit_id: string;
  unit_name?: string;
  seasonTeamId?: string;
  category?: 'FIRST' | 'RESERVE' | 'YOUTH' | 'OTHER';
  ageClass?: number | null;
  division?: TeamDivision;
  /** Ob die anmeldende Person diese Mannschaft führt. Entscheidet der Server. */
  canManage: boolean;
  capabilities: { canRespondSelf: boolean; canReadParticipants: boolean; canRespondForOthers: boolean; canRecordAttendance: boolean; canConfirm: boolean; canReadReasons: boolean; canReadReport: boolean; canEditEvent: boolean };
  playerCount?: number;
  occasionalCount?: number;
  nextSession?: { title: string; startAt: string; kind: string } | null;
}

export interface PersonalAccessLink { person_id: string; first_name: string; last_name: string; link_id: string | null; expires_at: string | null; last_used_at: string | null; claim_id: string | null; claim_email: string | null; claimed_at: string | null;
  /** Geraete mit Push - ueber den persoenlichen Link bzw. einen Zugang. */
  push_link: number; push_account: number;
  /** Zuletzt geoeffnet - ueber den Link oder eines seiner Geraete. */
  last_seen_at: string | null;
  phone: string | null; email: string | null }

export interface TeamAbsence {
  id: string;
  person_id: string;
  first_name: string;
  last_name: string;
  /** Ohne Recht auf Gründe `null`. */
  reason: string | null;
  note: string | null;
  from_date: string;
  to_date: string;
  source: 'MANAGER' | 'PERSONAL_LINK';
}

/** "2026-09-15" als "15.09.2026", ohne Umweg über eine Zeitzone. */
export function formatDay(value: string): string {
  const [year, month, day] = value.split('-');
  return `${day}.${month}.${year}`;
}

/** Heutiges Datum als "YYYY-MM-DD" in Ortszeit. */
export function today(offsetDays = 0): string {
  const datum = new Date(Date.now() + offsetDays * 86_400_000);
  datum.setMinutes(datum.getMinutes() - datum.getTimezoneOffset());
  return datum.toISOString().slice(0, 10);
}
export interface GeneratedPersonalAccessLink { id: string; personId: string; name: string; firstName: string; phone: string | null; email: string | null; token: string; expiresAt: string }

export interface Season {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

export interface RosterRow {
  person_id: string;
  first_name: string;
  last_name: string;
  jersey_number: number | null;
  position: string;
  status: RosterStatus;
  /** YYYY-MM-DD */
  birth_date: string | null;
  /** Zusatzinfos am Kadereintrag, z. B. "U16" oder "Schichtarbeit". */
  tags: string[];
}

export const TEAM_DIVISION_LABELS: Record<TeamDivision, string> = {
  MEN: 'Herren',
  WOMEN: 'Damen',
  MIXED: 'Mixed',
};

/**
 * Altersklasse, in die ein Jahrgang in einer Saison faellt - dieselbe Regel
 * wie die Jahrgangsplanung: Saisonbeginn 2026, Jahrgang 2014 ergibt U13.
 */
export function ageClassFor(birthDate: string, seasonStartDate: string): number {
  return Number(seasonStartDate.slice(0, 4)) - Number(birthDate.slice(0, 4)) + 1;
}

/** Alter in vollen Jahren an einem Stichtag (YYYY-MM-DD). */
export function ageOn(birthDate: string, day: string): number {
  const years = Number(day.slice(0, 4)) - Number(birthDate.slice(0, 4));
  return day.slice(5) < birthDate.slice(5) ? years - 1 : years;
}

/**
 * Farben des Teilnahmebands - dieselben beim Trainer wie im persönlichen Link.
 *
 * Die Farbe steht nie allein: daneben steht immer das Wort aus
 * ATTENDANCE_BAND_LABELS, damit das Band auch bei Farbsehschwäche und im
 * Ausdruck lesbar bleibt.
 */
export const ATTENDANCE_BAND_STYLE: Record<AttendanceBand, { punkt: string; feld: string }> = {
  GREAT: { punkt: 'bg-emerald-700', feld: 'bg-emerald-700 text-white' },
  GOOD: { punkt: 'bg-emerald-400', feld: 'bg-emerald-100 text-emerald-900' },
  FAIR: { punkt: 'bg-amber-500', feld: 'bg-amber-100 text-amber-900' },
  LOW: { punkt: 'bg-rose-600', feld: 'bg-rose-100 text-rose-900' },
  PAUSED: { punkt: 'bg-slate-400', feld: 'bg-slate-100 text-slate-700' },
  UNKNOWN: { punkt: 'bg-slate-300', feld: 'bg-slate-100 text-slate-600' },
};

export const ROSTER_STATUS_LABELS: Record<RosterStatus, string> = {
  ACTIVE: 'Stammkader',
  OCCASIONAL: 'Aushilfe / inaktiv',
};

export interface Session {
  id: string;
  title: string;
  description: string | null;
  start_at: string;
  end_at: string;
  location: string;
  status: string;
  /** Terminart: Training oder Arbeitseinsatz. */
  kind?: 'TRAINING' | 'WORK';
  confirmed_at: string | null;
  /** Gemeinsames Training einer anderen Mannschaft: deren Name. */
  guest_of?: string | null;
  /** Zu diesem Training eingeladene Mannschaften. */
  guest_teams?: string[];
}

export interface Match {
  id: string;
  teamId?: string | null;
  teamName?: string | null;
  homeTeam: string;
  awayTeam: string;
  startAt: string;
  competition: string | null;
  isHome: boolean;
  status?: 'SCHEDULED' | 'POSTPONED' | 'CANCELLED' | 'PLAYED';
  homeScore?: number | null;
  awayScore?: number | null;
}

export interface Shift {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  capacity: number;
  confirmed: number;
  myStatus: string | null;
  status: string;
}

/** Auswertung der Anwesenheit über eine Saison. */
export interface AttendanceSummary {
  zeitraum: { from: string; to: string } | null;
  termine: { gesamt: number; gewertet: number; kommend: number; bestaetigt: number; unbestaetigt: number };
  gesamt: {
    anwesend: number;
    abgemeldet: number;
    unentschuldigt: number;
    verletzt: number;
    erfasst: number;
    quote: number | null;
  };
  spieler: {
    personId: string;
    name: string;
    jerseyNumber: number | null;
    rosterStatus: RosterStatus;
    gastVon: string | null;
    anderswo: number;
    anwesend: number;
    abgemeldet: number;
    unentschuldigt: number;
    verletzt: number;
    /** Entschuldigt gefehlt - zählt nicht gegen die Person. */
    entschuldigt: number;
    band: AttendanceBand;
    erfasst: number;
    offen: number;
    quote: number | null;
  }[];
  /** Aushilfen im Kader; ob sie in `spieler` stehen, sagt `aushilfenEingeblendet`. */
  aushilfen: number;
  aushilfenEingeblendet: boolean;
}

/** Wert für ein `datetime-local`-Feld, um `tage` in der Zukunft. */
export function localDateTime(tage = 1): string {
  const datum = new Date(Date.now() + tage * 86_400_000);
  datum.setMinutes(datum.getMinutes() - datum.getTimezoneOffset());
  return datum.toISOString().slice(0, 16);
}

/**
 * Verschiebt einen `datetime-local`-Wert um Minuten.
 *
 * Gebraucht fuer die Vorgabe des Endes: ein Termin, der endet, wenn er
 * beginnt, wird vom Server abgewiesen - und die Meldung erschien bisher erst
 * beim Speichern.
 */
export function plusMinutes(wert: string, minuten: number): string {
  const datum = new Date(wert);
  datum.setMinutes(datum.getMinutes() + minuten - datum.getTimezoneOffset());
  return datum.toISOString().slice(0, 16);
}

export function iso(wert: string): string {
  return new Date(wert).toISOString();
}
